function getNodeId(prefix, line) {
  return `${prefix}:${line}`;
}

function getNodeRaw(node) {
  return node.raw || node.expression || node.text || '';
}

function isLabelNode(node) {
  if (node.lineClassification && node.lineClassification.type !== 'unknown') {
    return false;
  }

  return node.type === 'unknown' && /^[A-Z][A-Z0-9_]*:?$/i.test(node.raw);
}

function normalizeLabel(label) {
  return label.replace(/:$/, '');
}

function hasEditableFields(form) {
  if (typeof form.fieldCount === 'number') {
    return form.fieldCount > 0;
  }

  return (form.fields || []).some((field) => {
    if (field.visible === false || field.capturable === false || field.readonly || field.disabled) {
      return false;
    }

    if (field.type === 'field_row') {
      return (field.parts || []).some((part) => {
        return (
          part.visible !== false &&
          part.capturable !== false &&
          !part.readonly &&
          !part.disabled &&
          (part.type === 'field' ||
            part.type === 'select' ||
            part.controlType === 'input' ||
            part.control === 'input' ||
            part.controlType === 'checkbox' ||
            part.control === 'checkbox' ||
            part.controlType === 'date' ||
            part.controlType === 'hour' ||
            part.controlType === 'mail' ||
            part.controlType === 'button_menu')
        );
      });
    }

    return (
      field.type === 'field' ||
      field.type === 'select' ||
      field.controlType === 'input' ||
      field.control === 'input' ||
      field.type === 'checkbox-group' ||
      field.controlType === 'checkbox' ||
      field.control === 'checkbox' ||
      field.controlType === 'date' ||
      field.controlType === 'hour' ||
      field.controlType === 'mail' ||
      field.controlType === 'button_menu'
    );
  });
}

function hasButtons(form) {
  if (typeof form.buttonCount === 'number') {
    return form.buttonCount > 0;
  }

  return (form.fields || []).some((field) => field.type === 'button');
}

function hasMessage(form) {
  if (typeof form.messageCount === 'number') {
    return form.messageCount > 0;
  }

  return (form.fields || []).some((field) => field.type === 'text' || field.type === 'system' || field.label || field.text);
}

function findFormByWindow(forms, windowNode) {
  return forms.find((form) => form.id === windowNode.id && form.line === windowNode.line) || null;
}

function findWindowEndIndex(nodes, startIndex) {
  return nodes.findIndex((node, index) => index > startIndex && node.type === 'endwindow');
}

function findReturnGoto(nodes, startIndex) {
  for (let index = startIndex + 1; index < nodes.length; index += 1) {
    const node = nodes[index];

    if (node.type === 'endif' || node.type === 'window' || node.type === 'if') {
      return null;
    }

    if (node.type === 'goto') {
      return node;
    }
  }

  return null;
}

function createFlowNode(node, extra = {}) {
  return {
    id: getNodeId(node.type, node.line),
    type: node.type,
    line: node.line,
    label: extra.label || node.id || node.expression || getNodeRaw(node),
    raw: getNodeRaw(node),
    ...extra,
  };
}

function addNode(flowGraph, node) {
  if (flowGraph.nodes.some((candidate) => candidate.id === node.id)) {
    return;
  }

  flowGraph.nodes.push(node);
}

function addEdge(flowGraph, from, to, type = 'next', label = null) {
  if (!from || !to) {
    return;
  }

  flowGraph.edges.push({
    from,
    to,
    type,
    label,
  });
}

function classifyFormByFlow(form, flowInfo) {
  if (hasEditableFields(form)) {
    return 'form';
  }

  if (hasButtons(form)) {
    return 'confirmation';
  }

  return 'validation';
}

function cloneIr(ir) {
  return JSON.parse(JSON.stringify(ir));
}

export function analyzeFlow(ast, ir) {
  const nextIr = cloneIr(ir);
  const flowGraph = {
    nodes: [
      {
        id: 'start',
        type: 'start',
        label: 'START',
        line: 0,
      },
    ],
    edges: [],
  };
  const ifStack = [];
  const windowFlow = {};
  let previousFlowNodeId = 'start';

  ast.nodes.forEach((node, index) => {
    if (node.type === 'endif') {
      ifStack.pop();
      return;
    }

    if (node.type === 'if') {
      const flowNode = createFlowNode(node, {
        type: 'if',
        label: node.expression,
      });
      addNode(flowGraph, flowNode);
      addEdge(flowGraph, previousFlowNodeId, flowNode.id, 'next');
      ifStack.push(flowNode.id);
      previousFlowNodeId = flowNode.id;
      return;
    }

    if (node.type === 'window') {
      const form = findFormByWindow(nextIr.forms, node);
      const endIndex = findWindowEndIndex(ast.nodes, index);
      const returnGoto = findReturnGoto(ast.nodes, endIndex === -1 ? index : endIndex);
      const activationIf = ifStack[ifStack.length - 1] || null;
      const flowInfo = {
        activation: activationIf ? 'if' : 'direct',
        activatedBy: activationIf,
        returnsTo: returnGoto ? returnGoto.expression : null,
      };
      const flowNode = createFlowNode(node, {
        type: 'window',
        label: node.title || node.id,
        windowId: node.id,
        title: node.title,
      });

      addNode(flowGraph, flowNode);
      addEdge(flowGraph, activationIf || previousFlowNodeId, flowNode.id, activationIf ? 'ifTrue' : 'next', activationIf ? 'TRUE' : null);

      if (returnGoto) {
        const gotoNode = createFlowNode(returnGoto, {
          type: 'goto',
          label: returnGoto.expression,
        });
        const labelNode = {
          id: getNodeId('label', returnGoto.expression),
          type: 'label',
          label: returnGoto.expression,
        };

        addNode(flowGraph, gotoNode);
        addNode(flowGraph, labelNode);
        addEdge(flowGraph, flowNode.id, gotoNode.id, 'returns');
        addEdge(flowGraph, gotoNode.id, labelNode.id, 'goto');
      }

      if (form) {
        windowFlow[`${form.id}:${form.line}`] = flowInfo;
        form.activation = flowInfo.activation;
        form.activatedBy = flowInfo.activatedBy;
        form.returnsTo = flowInfo.returnsTo;
        form.windowType = classifyFormByFlow(form, flowInfo);
      }

      previousFlowNodeId = flowNode.id;
      return;
    }

    if (node.type === 'call') {
      const callNode = createFlowNode(node, {
        type: 'call',
        label: node.expression,
      });
      const subroutineNode = {
        id: getNodeId('subroutine', node.expression),
        type: 'subroutine',
        label: node.expression,
      };

      addNode(flowGraph, callNode);
      addNode(flowGraph, subroutineNode);
      addEdge(flowGraph, previousFlowNodeId, callNode.id, 'next');
      addEdge(flowGraph, callNode.id, subroutineNode.id, 'call');
      previousFlowNodeId = callNode.id;
      return;
    }

    if (node.type === 'goto') {
      const gotoNode = createFlowNode(node, {
        type: 'goto',
        label: node.expression,
      });
      const labelNode = {
        id: getNodeId('label', node.expression),
        type: 'label',
        label: node.expression,
      };

      addNode(flowGraph, gotoNode);
      addNode(flowGraph, labelNode);
      addEdge(flowGraph, previousFlowNodeId, gotoNode.id, 'next');
      addEdge(flowGraph, gotoNode.id, labelNode.id, 'goto');
      previousFlowNodeId = gotoNode.id;
      return;
    }

    if (node.type === 'exit') {
      const exitNode = createFlowNode(node, {
        type: 'exit',
        label: 'EXIT',
      });

      addNode(flowGraph, exitNode);
      addEdge(flowGraph, previousFlowNodeId, exitNode.id, 'next');
      previousFlowNodeId = exitNode.id;
      return;
    }

    if (node.type === 'label') {
      const labelNode = {
        id: getNodeId('label', normalizeLabel(node.name)),
        type: 'label',
        label: normalizeLabel(node.name),
        line: node.line,
        raw: node.raw,
      };

      addNode(flowGraph, labelNode);
      addEdge(flowGraph, previousFlowNodeId, labelNode.id, 'next');
      previousFlowNodeId = labelNode.id;
      return;
    }

    if (isLabelNode(node)) {
      const labelNode = {
        id: getNodeId('label', normalizeLabel(node.raw)),
        type: 'label',
        label: normalizeLabel(node.raw),
        line: node.line,
        raw: node.raw,
      };

      addNode(flowGraph, labelNode);
      addEdge(flowGraph, previousFlowNodeId, labelNode.id, 'next');
      previousFlowNodeId = labelNode.id;
    }
  });

  return {
    ir: nextIr,
    flowGraph,
    windowFlow,
  };
}
